import React, { useContext, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import StateContext from 'common/contexts/state-context';
import MapContext from 'common/contexts/map-context';
import TrackingService from 'common/services/Tracking/Tracking';
import useAutoUserGeolocation from 'common/customHooks/useAutoUserGeolocation';

import HeroSearch from './HeroSearch/HeroSearch';
import LocatorLayout from './LocatorLayout/LocatorLayout';
import CityLinks from './CityLinks/CityLinks';
import PromoWidget from './widgets/PromoWidget';
import PricesWidget from './widgets/PricesWidget';
import NetworkReviews from './widgets/NetworkReviews';
import ReassuranceBar from '../../ui/ReassuranceBar';
import { USE_WP_CHROME } from '../../GlobalValues';
import { searchAroundUser, searchByPlaceName, searchByPrediction } from '../../services/search';

/**
 * Page listing — carte + liste (§7.2 du plan). `slug` vient de
 * `/location/:slug` : la résolution se fait côté serveur (search_shortcuts),
 * qui pose alors `state.sl.home_search = false` et remplit `merchants`.
 *
 * `useAutoUserGeolocation` reprend le comportement de 2025_VOG : si la marque
 * a activé la géolocalisation automatique et qu'on est sur `/` en attente
 * d'une recherche, on redirige vers `/location/<ville-de-l'utilisateur>` sans
 * attendre de clic.
 */
const StoreLocator = () => {
  const [state, setState] = useContext(StateContext);
  const [map, setMap] = useContext(MapContext);
  const { slug } = useParams();
  const [isBusy, setBusy] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Pendant du `pageView('localpage')` de la fiche salon (`PlTracking(..., 'pageview')`) :
  // sans lui, la moitié listing du tunnel n'existe pas dans les stats Placeloop.
  // 2025_VOG le pose dans `HomeSearch` ; ici il est remonté d'un cran, avant les
  // deux retours anticipés ci-dessous, pour que l'appel du hook ne soit jamais
  // conditionnel.
  useEffect(() => {
    TrackingService.pageView('storelocator');
  }, []);

  const autoRedirectUrl = useAutoUserGeolocation();
  if (autoRedirectUrl) {
    window.location.href = autoRedirectUrl;
    return null;
  }

  if (!state.sl) return null;

  /**
   * Une recherche met à jour la liste **et** recadre la carte : `common` cadre
   * sur `map.placeLoc` (mécanisme de 2025_VOG). Les deux `set*` sont dans le
   * même gestionnaire, donc groupés en un seul rendu — l'effet de la carte voit
   * déjà les nouveaux salons quand il recadre.
   */
  const applyResult = (result) => {
    if (!result) return;
    setNotFound(Boolean(result.notFound));
    // Rien trouvé (`searchByPlaceName`, search.js:62) : `result.state` est
    // l'état d'avant-recherche, inchangé — inutile de le réappliquer, et ça
    // effacerait `searchOrigin` d'une recherche précédente encore valide.
    if (result.notFound) return;
    setState({ ...result.state, searchOrigin: result.origin });
    if (result.origin) {
      setMap({
        ...map,
        placeId: result.placeId,
        placeLoc: { ...map.placeLoc, lat: result.origin.lat, lng: result.origin.lng },
      });
    }
  };

  const runSearch = async (query) => {
    setBusy(true);
    try {
      applyResult(await searchByPlaceName(state, query));
    } catch (error) {
      console.warn('La recherche a échoué', error);
    } finally {
      setBusy(false);
    }
  };

  const runGeolocation = async () => {
    setBusy(true);
    try {
      applyResult(await searchAroundUser(state));
    } catch (error) {
      console.warn('La géolocalisation a échoué', error);
    } finally {
      setBusy(false);
    }
  };

  const runSelectPrediction = async (prediction) => {
    setBusy(true);
    try {
      applyResult(await searchByPrediction(state, prediction));
    } catch (error) {
      console.warn('La recherche a échoué', error);
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="locator-page" id="locator-page">
      <div className="container">
        <HeroSearch
          slug={slug}
          onSearch={runSearch}
          onSelectPrediction={runSelectPrediction}
          onGeolocate={runGeolocation}
          isBusy={isBusy}
        />
      </div>

      <LocatorLayout merchants={state.merchants} notFound={notFound} />

      <CityLinks />
      <PromoWidget />
      <PricesWidget />
      <NetworkReviews />
      {/* Le footer WordPress porte déjà la réassurance (`.site-rass`) : on ne
          rend la version React que quand le chrome React est actif. */}
      {!USE_WP_CHROME && <ReassuranceBar />}
    </section>
  );
};

export default StoreLocator;
